import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Building2, MapPin, Users, ClipboardCheck } from 'lucide-react';
import { PermitStatusChart, TodayOnSiteCard } from '../components/dashboard/PermitWidgets';
import { TopJobTypesChart, ApprovalTimeChart } from '../components/dashboard/IsgCharts';
import { useScreenReady } from '../lib/perfScreen';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ title, value, icon: Icon, color, description, onClick }) => (
  <Card className={`border-slate-200 card-hover ${onClick ? 'cursor-pointer' : ''}`} onClick={onClick}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
          {description && (
            <p className="text-xs text-slate-400 mt-1">{description}</p>
          )}
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const DashboardPage = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState({});
  const [period, setPeriod] = useState('all');
  const [loading, setLoading] = useState(true);
  useScreenReady('dashboard', !loading);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    fetchSummary();
    axios.get(`${API}/locations`).then(r => setLocations(r.data)).catch(() => {});
  }, []);
  const cards = summary.cards || {};
  const usedIds = new Set(summary.used_location_ids || []);
  const usedLocations = locations.filter(l => usedIds.has(l.id)).sort((a, b) => a.name.localeCompare(b.name, 'tr'));

  // Kartlar, grafikler ve "Bugün Sahada" sunucuda (yetki kapsamında) özetlenir; tüm talepler indirilmez
  const fetchSummary = async () => {
    try {
      const { data } = await axios.get(`${API}/dashboard/summary`);
      setSummary(data);
    } catch (error) {
      console.error('Failed to fetch dashboard summary:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-slate-500 mt-1">Genel bakış ve istatistikler</p>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Toplam İşletme"
          value={cards.total_companies ?? '–'}
          icon={Building2}
          color="bg-slate-900"
          onClick={() => navigate('/companies')}
        />
        <StatCard
          title="Lokasyonlar"
          value={cards.total_locations ?? '–'}
          icon={MapPin}
          color="bg-orange-500"
          onClick={() => navigate('/locations')}
        />
        <StatCard
          title="Aktif Personel"
          value={cards.active_employees ?? '–'}
          icon={Users}
          color="bg-emerald-500"
          description={`${cards.total_employees ?? '–'} toplam personel`}
          onClick={() => navigate('/employees')}
        />
        <StatCard
          title="Talep İşlemleri"
          value={cards.ongoing ?? 0}
          icon={ClipboardCheck}
          color="bg-blue-500"
          description={`Onaylı, eksik/hatalı ve bekleyen · ${cards.total_permits ?? 0} toplam talep`}
          onClick={() => navigate('/work-permits')}
        />
      </div>

      <TodayOnSiteCard list={summary.today || []} onClick={() => { localStorage.setItem('work-permits-view', 'calendar'); navigate('/work-permits'); }} />

      <PermitStatusChart stats={summary.status} period={period} setPeriod={setPeriod} onDetail={() => navigate('/work-permits')}
        onBarClick={(group) => { localStorage.setItem('work-permits-view', 'list'); navigate('/work-permits', { state: { dashGroup: group, dashPeriod: period } }); }} />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <TopJobTypesChart jobTypes={summary.job_types || {}} locations={usedLocations} />
        <ApprovalTimeChart stats={summary.approval || {}} locations={usedLocations} />
      </div>
    </div>
  );
};

export default DashboardPage;
